import { useState } from 'react';
import { toast } from 'sonner';
import {
  CircleAlert,
  CircleCheck,
  FileText,
  Loader2,
  Lock,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { t } from '@/i18n';
import { useMileageApi } from '@/services/api';
import type {
  MileageRecordDetail,
  MileageEntrySource,
  ReconciliationResult,
  ReconciliationSuggestion,
  VehicleListItem,
} from '@ip/shared-types';

const ALERT_SUCCESS_CLASS =
  'border-emerald-500/50 text-emerald-800 dark:text-emerald-300 [&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400';
const ALERT_WARNING_CLASS =
  'border-amber-500/50 text-amber-800 dark:text-amber-300 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400';
const ALERT_ERROR_CLASS = 'border-destructive/50';

interface ReconciliationPanelProps {
  companyNip: string;
  record: MileageRecordDetail;
  vehicleId: string;
  vehicles: VehicleListItem[];
  onEntryCreated: () => void;
}

export function ReconciliationPanel({ companyNip, record, vehicleId, vehicles, onEntryCreated }: ReconciliationPanelProps) {
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const { reconcile, createEntry, downloadRecordPdf, finalizeRecord } = useMileageApi();

  const handleReconcile = async () => {
    try {
      setLoading(true);
      const data = await reconcile(companyNip, record.id);
      setResult(data);
    } catch (error) {
      console.error('Reconciliation failed:', error);
      toast.error('Reconciliation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: ReconciliationSuggestion) => {
    if (suggestion.type !== 'add_trip') return;

    const vehicle = vehicles.find(v => v.id === vehicleId);
    const defaultDriver = vehicle?.drivers.find(d => d.isDefault) || vehicle?.drivers[0];

    try {
      await createEntry(companyNip, record.id, {
        tripDate: `${record.year}-${String(record.month).padStart(2, '0')}-15`,
        routeDescription: suggestion.routeDescription || 'Przejazd służbowy',
        purpose: suggestion.purpose || 'Przejazd służbowy',
        distanceKm: suggestion.distanceKm,
        driverName: defaultDriver?.fullName || '',
        isRoundTrip: true,
        invoiceId: suggestion.invoiceId,
        source: (suggestion.source as MileageEntrySource) || 'manual',
      });
      toast.success('Trip added');
      onEntryCreated();
      // Re-run reconciliation
      const data = await reconcile(companyNip, record.id);
      setResult(data);
    } catch (error) {
      console.error('Failed to accept suggestion:', error);
      toast.error(t('errors.saveFailed'));
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  // Saves the rendered PDF (was: preview in a new tab — window.open is
  // blocked in some hosts, see utils/download.ts). Failures toast inside
  // downloadRecordPdf; session expiry is handled by the global modal.
  const handlePreviewPdf = async () => {
    try {
      setPdfLoading(true);
      await downloadRecordPdf(
        companyNip,
        record.id,
        `ewidencja-przebiegu-${record.year}-${String(record.month).padStart(2, '0')}.pdf`,
      );
    } catch {
      /* session expired — handled by the global modal */
    } finally {
      setPdfLoading(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      await finalizeRecord(companyNip, record.id);
      toast.success('Record finalized and PDF uploaded');
      onEntryCreated(); // Refresh
    } catch (error) {
      console.error('Finalization failed:', error);
      toast.error('Finalization failed');
    } finally {
      setFinalizing(false);
    }
  };

  const targetKm = record.odometerEnd != null && record.odometerStart != null
    ? record.odometerEnd - record.odometerStart
    : null;
  const percent = targetKm && targetKm > 0
    ? Math.min(100, Math.round((record.entriesKm / targetKm) * 100))
    : 0;
  const delta = targetKm != null ? targetKm - record.entriesKm : null;

  const progressColor = delta != null && Math.abs(delta) < 5
    ? 'bg-emerald-500'
    : delta != null && delta > 0
      ? 'bg-amber-500'
      : 'bg-red-500';

  return (
    <Card className="mt-4 py-4">
      <CardContent className="flex flex-col gap-3 px-4">
        {/* Progress bar */}
        <div>
          <span className="text-sm font-medium">
            {t('mileage.records.entriesKm')}: {Math.round(record.entriesKm)} km
            {targetKm != null && ` / ${targetKm} km`}
          </span>
          {targetKm != null && (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="bg-muted h-2 flex-1 overflow-hidden rounded-full">
                <div
                  className={`h-full rounded-full transition-all ${progressColor}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {delta != null ? (delta >= 0 ? '+' : '') + Math.round(delta) : '?'} km
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReconcile}
            disabled={loading || record.odometerEnd == null}
          >
            {loading ? <Loader2 className="size-4 animate-spin" /> : <Zap className="size-4" />}
            Reconcile
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviewPdf}
            disabled={pdfLoading || record.entries.length === 0}
          >
            {pdfLoading ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
            Preview PDF
          </Button>
          <Button
            size="sm"
            onClick={handleFinalize}
            disabled={
              finalizing ||
              record.status === 'finalized' ||
              record.entries.length === 0 ||
              record.odometerEnd == null
            }
          >
            {finalizing ? <Loader2 className="size-4 animate-spin" /> : <Lock className="size-4" />}
            Finalize
          </Button>
          {record.status === 'finalized' && (
            <Badge className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
              <CircleCheck className="size-3" />
              Finalized
            </Badge>
          )}
        </div>

        {/* Reconciliation result */}
        {result && (
          <div>
            {result.status === 'balanced' && (
              <Alert className={ALERT_SUCCESS_CLASS}>
                <CircleCheck className="size-4" />
                <AlertTitle>Balanced</AlertTitle>
                <AlertDescription>
                  {`Entries (${result.currentKm} km) match odometer (${result.targetKm} km) within tolerance.`}
                </AlertDescription>
              </Alert>
            )}
            {result.status === 'missing_odometer' && (
              <Alert className={ALERT_WARNING_CLASS}>
                <TriangleAlert className="size-4" />
                <AlertTitle>Missing odometer</AlertTitle>
                <AlertDescription>Set the odometer end value to reconcile.</AlertDescription>
              </Alert>
            )}
            {(result.status === 'under_logged' || result.status === 'over_logged') && (
              <>
                <Alert
                  variant={result.status === 'under_logged' ? undefined : 'destructive'}
                  className={`mb-2 ${result.status === 'under_logged' ? ALERT_WARNING_CLASS : ALERT_ERROR_CLASS}`}
                >
                  {result.status === 'under_logged' ? (
                    <TriangleAlert className="size-4" />
                  ) : (
                    <CircleAlert className="size-4" />
                  )}
                  <AlertTitle>
                    {result.status === 'under_logged' ? 'Under-logged' : 'Over-logged'}
                  </AlertTitle>
                  <AlertDescription>
                    {`Delta: ${result.deltaKm} km. ${result.suggestions.length} suggestion(s).`}
                  </AlertDescription>
                </Alert>
                {result.suggestions.map((s, i) => (
                  <Card key={i} className="mb-1 py-2">
                    <CardContent className="flex items-center justify-between gap-2 px-3">
                      <div className="flex flex-col">
                        <span className="text-sm">{s.description}</span>
                        <span className="text-muted-foreground flex items-center gap-2 text-sm">
                          {s.distanceKm > 0 ? '+' : ''}{s.distanceKm} km
                          {s.confidence != null && (
                            <Badge
                              variant={s.confidence >= 0.5 ? 'outline' : 'secondary'}
                              className={
                                s.confidence >= 0.7
                                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                  : s.confidence >= 0.5
                                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                                    : undefined
                              }
                            >
                              {Math.round(s.confidence * 100)}%
                            </Badge>
                          )}
                        </span>
                      </div>
                      {s.type === 'add_trip' && (
                        <Button size="sm" onClick={() => handleAcceptSuggestion(s)}>
                          Accept
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
